import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import { useDispatch, useSelector } from 'react-redux'
import { Button, Stack, Typography, CircularProgress } from '@mui/material'
import { Fingerprint as FingerprintIcon, CheckCircle as CheckCircleIcon, CloudUpload } from '@mui/icons-material'
import { useTheme } from '@mui/styles'
import { useGetOrgImageQuery, useUploadOrgImageMutation } from 'services/assets'
import { dispatchSnackbar, b64toBlob } from 'utils/helpers'
import DialogModal from '.'

const CaptureFingerprintModal = ({ onSave, onClose, open, fingerprintImage, ...restProps }) => {
  const dispatch = useDispatch()
  const theme = useTheme()
  const imageStyle = {
    color: theme.palette.text.hint,
    height: 120,
    width: 120,
    objectFit: 'contain',
    border: 1,
    borderStyle: 'solid',
    borderColor: theme.palette.text.disabled,
  }

  const { data: orgImage } = useGetOrgImageQuery(
    {
      filename: fingerprintImage?.filename,
      isTemp: false,
    },
    { skip: !fingerprintImage?.filename },
  )

  const [uploadOrgImage, { isLoading: isUploadingOrgImage }] = useUploadOrgImageMutation()
  const { enableUploadFingerprint } = useSelector(state => state.featureFlags?.variables || {})

  const [src, setSrc] = useState('')
  const [isViewMode, setIsViewMode] = useState(false)
  const [file, setFile] = useState(null)

  const startListeningForFingerprint = async () => {
    try {
      const uri = 'https://localhost:8443/SGIFPCapture'
      const response = await fetch(uri, {
        method: 'POST',
        body: `Timeout=10000&Quality=50&licstr=${process.env.REACT_APP_SECUGEN_LICENSE_KEY}&templateFormat=ISO&imageWSQRate=0.75`,
      })
      const data = await response.json()
      if (data?.ErrorCode !== 0) {
        const error = new Error()
        error.code = data?.ErrorCode
        throw error
      }
      setSrc(`data:image/bmp;base64,${data.BMPBase64}`)
    } catch (error) {
      const errorCodeSuffix = `(Error code: ${error.code || 1})`
      const errorMessage = `There was an error connecting to a fingerprint scanner ${errorCodeSuffix}`
      dispatchSnackbar(dispatch, 'error', errorMessage)
      if (!enableUploadFingerprint) {
        onClose()
      }
    }
  }

  const handleFileChange = event => {
    const file = event.target.files[0]
    if (file) {
      const reader = new FileReader()
      reader.onload = e => setSrc(e.target.result)
      reader.readAsDataURL(file)
      setFile(file)
      setIsViewMode(false) // Set to false to enable submit button
    }
  }

  const onSubmit = async () => {
    try {
      const imageBlob = file ? file : b64toBlob(src, 'image/bmp')
      const fingerprintFormData = new FormData()
      fingerprintFormData.append('image', imageBlob)

      const response = await uploadOrgImage({ body: fingerprintFormData }).unwrap()
      onSave(response.imageName)
      dispatchSnackbar(dispatch, 'success', 'Fingerprint uploaded successfully.')
    } catch (error) {
      const errorMessage = error?.data?.error || 'There was an error processing the fingerprint'
      dispatchSnackbar(dispatch, 'error', errorMessage)
    }
    onClose()
  }

  const onRetake = () => {
    setSrc('')
    setFile(null)
    setIsViewMode(false)
    startListeningForFingerprint()
  }

  const renderMessage = () => {
    if (isUploadingOrgImage) {
      return <CircularProgress size="24px" />
    }
    if (src) {
      return (
        <>
          <CheckCircleIcon color="primary" />
          <Typography>Fingerprint captured</Typography>
        </>
      )
    }
    return (
      <Typography>
        Scan your fingerprint now
        {enableUploadFingerprint && ' or upload a fingerprint image...'}
      </Typography>
    )
  }

  useEffect(() => {
    if (orgImage) {
      setSrc(orgImage)
      setIsViewMode(true)
    }
  }, [orgImage])

  useEffect(() => {
    if (open === true && !fingerprintImage?.filename) {
      startListeningForFingerprint()
    }
  }, [open])

  return (
    <DialogModal
      {...restProps}
      open={open}
      onClose={() => {
        if (isUploadingOrgImage) return
        onClose()
      }}
      onSave={isViewMode ? onClose : onSubmit}
      extraAction={{
        text: 'Retry',
        onClick: onRetake,
        disabled: !src,
      }}
      saveDisabled={!src}
      buttonTitle="Done"
    >
      <Stack alignItems="center">
        {src ? <img src={src} alt="fingerprint" style={imageStyle} /> : <FingerprintIcon style={imageStyle} />}
        {enableUploadFingerprint && (
          <>
            <input
              id="file-input"
              type="file"
              style={{ display: 'none' }}
              accept="image/*"
              onChange={handleFileChange}
            />
            {isViewMode ? null : (
              <Button
                startIcon={<CloudUpload />}
                onClick={() => document.getElementById('file-input').click()}
                disabled={isViewMode}
                sx={{ mt: 2 }}
              >
                Upload Fingerprint
              </Button>
            )}
          </>
        )}
        <Stack flexDirection="row" mt={2}>
          {renderMessage()}
        </Stack>
      </Stack>
    </DialogModal>
  )
}

CaptureFingerprintModal.propTypes = {
  ...DialogModal.propTypes,
  onSave: PropTypes.func,
  open: PropTypes.bool,
  fingerprintImage: PropTypes.object,
}

CaptureFingerprintModal.defaultProps = {
  ...DialogModal.defaultProps,
  onSave: () => {},
  open: true,
  fingerprintImage: {},
}

export default CaptureFingerprintModal
